import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, Waves, Package, Beaker } from "lucide-react";
import TopBar from "../components/shop/TopBar";
import Reveal from "../components/Reveal";
import SystemDiagram from "../components/SystemDiagram";
import { IMG } from "../data/content";

const LINKS = [
  { to: "/shop", label: "Pool Shop" },
  { to: "/shop/build", label: "Build Spec" },
  { to: "/home", label: "Home System" },
];

const MODULES = [
  { icon: Package, t: "Smart dosing module", d: "Metered acid / buffer delivery, fail-safe closed on fault." },
  { icon: Waves, t: "Chlorinator module", d: "Direct amperometric free-chlorine control of true cell output." },
  { icon: Cpu, t: "Sensor module", d: "Inline reagentless FC, pH, salt, temperature & turbidity." },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-[#FAFCFD]" data-testid="home-page">
      <TopBar links={LINKS} />
      <section className="relative pt-16 pb-16 md:pt-24 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sky-50/60 to-white" />
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-[1fr_1fr] gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-mono2 text-slate-600 mb-5">Project 2 · Roadmap</span>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Aqualux Nexus <span className="aqua-text">Home</span>
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-xl">
              The installed on-site system: a smart controller on a no-replumb inline backbone, with plug-and-play
              chemical feeder, doser and chlorinator modules. We'll design this after the shop analyzer is locked.
            </p>
            <Link to="/shop" className="mt-7 inline-flex items-center gap-2 bg-slate-900 hover:bg-sky-700 text-white px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="home-goto-shop-btn">
              Explore the Pool Shop unit first <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
            <img src={IMG.scene} alt="Aqualux Nexus Home installed system" className="w-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><p className="eyebrow mb-3">Planned architecture</p><h2 className="font-display text-3xl font-bold text-slate-900">One controller · one inline backbone · snap-on modules</h2></Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-4 md:p-8">
              <SystemDiagram />
            </div>
          </Reveal>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.t} delay={i * 0.06}>
                  <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 border border-sky-100 mb-3"><Icon className="h-5 w-5 text-sky-600" /></span>
                    <p className="font-display font-bold text-slate-900">{m.t}</p>
                    <p className="text-sm text-slate-500 mt-1">{m.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-8 flex items-center gap-2 text-sm text-slate-400"><Beaker className="h-4 w-4" /> Roadmap concept — detailed design to follow.</p>
        </div>
      </section>
    </div>
  );
}
